import { applyEvent, initialState, type MatchEvent, type ScoreState } from '@aiscoreboard/contracts';
import {
  clearSessionDeadline, SEAT_TOKEN_KEYS, SESSION_TIMEOUT_MS, sessionDeadline, setSessionDeadline, validSessionToken,
} from './sessionTimeout.ts';
import { withRequestTimeout } from './requestTimeout.ts';

const MATCH_SESSION_KEY = 'aiscoreboard.playerSessionMatch';
type MatchSession = { id: string; ended: boolean };

function currentMatch(): MatchSession | null {
  const raw = localStorage.getItem(MATCH_SESSION_KEY);
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
      && 'ended' in value && typeof value.ended === 'boolean') return { id: value.id, ended: value.ended };
    throw new Error('Invalid player match session');
  } catch (error) {
    console.warn('[session-timeout] Could not restore player match lifecycle', error);
    return null;
  }
}

export function playerMatchStarted(id: string): void {
  const current = currentMatch();
  if (current?.id === id && !current.ended) return;
  localStorage.setItem(MATCH_SESSION_KEY, JSON.stringify({ id, ended: false }));
  for (const key of SEAT_TOKEN_KEYS) {
    if (validSessionToken(key)) clearSessionDeadline(key);
  }
}

export function matchEndedAt(id: string, events: MatchEvent[]): number | null {
  const retracted = new Set(events.flatMap(event => event.type === 'event.retracted' ? [event.targetSeq] : []));
  let state = initialState(id);
  let endedAt: number | null = null;
  for (const event of events) {
    if (event.type === 'event.retracted' || retracted.has(event.seq)) continue;
    const next = applyEvent(state, event);
    if (state.status !== 'match_over' && next.status === 'match_over') {
      const at = Date.parse(event.at);
      endedAt = Number.isFinite(at) ? at : null;
    }
    if (next.status !== 'match_over') endedAt = null;
    state = next;
  }
  return endedAt;
}

export function playerMatchEnded(id: string): void {
  const current = currentMatch();
  if (current?.id === id && current.ended) return;
  localStorage.setItem(MATCH_SESSION_KEY, JSON.stringify({ id, ended: true }));
  const deadline = Date.now() + SESSION_TIMEOUT_MS;
  const sessions = SEAT_TOKEN_KEYS.map(key => ({ key, token: validSessionToken(key) }));
  for (const { key, token } of sessions) {
    if (token) setSessionDeadline(key, token, deadline);
  }
  if (!sessions.some(session => session.token)) return;
  // A board can reconnect after the match ended on a phone. Use the persisted
  // ending event rather than granting another five minutes on every reload.
  void withRequestTimeout(10_000, async signal => {
    const response = await fetch(`/api/matches/${encodeURIComponent(id)}/events`, { cache: 'no-store', signal });
    if (!response.ok) throw new Error(`Could not read match end time (${response.status})`);
    const { events } = await response.json() as { events: MatchEvent[] };
    const endedAt = matchEndedAt(id, events);
    if (endedAt === null) throw new Error('Match ending event is missing');
    const latest = currentMatch();
    if (latest?.id !== id || !latest.ended) return;
    for (const { key, token } of sessions) {
      if (token && sessionDeadline(key, token) === deadline) {
        setSessionDeadline(key, token, Math.min(deadline, endedAt + SESSION_TIMEOUT_MS));
      }
    }
  })
    .catch(error => console.warn('[session-timeout] Using observed match end time; event lookup failed', error));
}

export function observePlayerMatch(state: ScoreState): void {
  if (state.practice || state.status === 'idle') return;
  if (state.status === 'match_over') playerMatchEnded(state.matchId);
  else playerMatchStarted(state.matchId);
}
